import React from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, PerspectiveCamera } from '@react-three/drei'
import { PhysicsSetup } from '@systems/physics/PhysicsSetup'
import { Ground } from '@components/world/Ground'
import { BrickPlacer } from '@components/world/BrickPlacer'
import { CameraController } from './CameraController'
import { FPSMonitor } from './FPSMonitor'
import { isDebugMode, isPhysicsDisabled } from '@utils/debug'

/**
 * BrickCanvas Component
 *
 * Main 3D canvas component using react-three-fiber (R3F).
 * Sets up the complete 3D environment including:
 * - Canvas with shadow support
 * - PerspectiveCamera with optimal viewport settings
 * - OrbitControls for intuitive 3D navigation
 * - Rapier physics simulation with optimized settings (via PhysicsSetup)
 * - Lighting system (ambient + directional with shadows)
 * - BrickPlacer for interactive brick placement
 *
 * Physics Configuration:
 * - Uses PhysicsSetup component with brick-optimized settings
 * - Gravity: [0, -9.81, 0] for realistic stacking
 * - High solver iterations for stable connections
 * - Debug mode: Set VITE_PHYSICS_DEBUG=true to enable
 * - Can be disabled with ?noPhysics=true for performance profiling (Task 4.1.6)
 *
 * Interaction:
 * - Mouse hover shows preview brick at snapped grid position
 * - Click to place brick at preview position
 * - OrbitControls: Right-click drag to rotate, scroll to zoom, middle-click to pan
 *
 * @component
 */
export const BrickCanvas: React.FC<{ onFPSUpdate?: (fps: number) => void }> = ({ onFPSUpdate }) => {
  // Check if physics should be disabled (Task 4.1.6 - performance profiling)
  const physicsDisabled = isPhysicsDisabled()

  return (
    <Canvas
      shadows
      gl={{
        antialias: true,
        alpha: false,
        powerPreference: 'high-performance',
      }}
      dpr={[1, 2]} // Device pixel ratio: min 1, max 2 for performance
      style={{
        width: '100%',
        height: '100%',
        display: 'block',
        background: '#1a1a1a',
      }}
    >
      {/* Camera Configuration */}
      <PerspectiveCamera
        makeDefault
        position={[15, 15, 15]}
        fov={50}
        near={0.1}
        far={1000}
      />

      {/* Camera Controls */}
      <OrbitControls
        makeDefault
        enableDamping
        dampingFactor={0.05}
        minDistance={5}
        maxDistance={50}
        maxPolarAngle={Math.PI / 2} // Prevent camera from going below ground
        target={[0, 0, 0]}
        enablePan={true}
        panSpeed={0.5}
        rotateSpeed={0.5}
        zoomSpeed={0.8}
      />

      {/* Camera Controller - Smooth preset transitions (Task 3.2.5) */}
      <CameraController />

      {/* FPS Monitor - Task 4.1.0 (only active in debug mode) */}
      {isDebugMode() && onFPSUpdate && <FPSMonitor onFPSUpdate={onFPSUpdate} />}

      {/* Lighting Setup */}
      {/* Ambient light for base illumination */}
      <ambientLight intensity={0.4} />

      {/* Directional light for shadows and depth */}
      <directionalLight
        position={[10, 20, 5]}
        intensity={0.8}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-far={50}
        shadow-camera-left={-20}
        shadow-camera-right={20}
        shadow-camera-top={20}
        shadow-camera-bottom={-20}
        shadow-bias={-0.0001}
      />

      {/* Hemisphere light for softer ambient lighting */}
      <hemisphereLight
        color="#ffffff"
        groundColor="#666666"
        intensity={0.3}
      />

      {/* Physics World - Optimized configuration for brick building */}
      {/* Task 4.1.6: Conditionally disable physics with ?noPhysics=true for performance profiling */}
      {physicsDisabled ? (
        <>
          {/* Ground Component - includes plane, grid, and collider */}
          <Ground />

          {/* BrickPlacer - Interactive brick placement system */}
          <BrickPlacer />
        </>
      ) : (
        <PhysicsSetup>
          {/* Ground Component - includes plane, grid, and collider */}
          <Ground />

          {/* BrickPlacer - Interactive brick placement system */}
          <BrickPlacer />
        </PhysicsSetup>
      )}
    </Canvas>
  )
}
